import React, { useCallback, useId, useMemo, useState } from 'react';
import { FieldCoordinates } from '../types/codeGeneration';
import {
  FieldSortPrefs,
  FieldSortKey,
  ReadingOrderMode,
  sortFields,
  orderForGeneration,
  listShowsGeneratedOrder,
  viewShowingGeneratedOrder,
} from '../utils/fieldSort';
import { useCollapsiblePanel } from '../hooks/useCollapsiblePanel';

/**
 * localStorage key for the editor list's collapsed state. Exported because
 * PDFViewer owns the state at the editor (the splitter needs to see it — see
 * the controlled `collapsed` prop) and has to name the same key.
 */
export const FIELD_LIST_COLLAPSED_KEY = 'pfbFieldListCollapsed';
const DEFAULT_COLLAPSED_KEY = FIELD_LIST_COLLAPSED_KEY;

export type FieldDirtyStatus = 'new' | 'modified' | 'saved';

export interface SidebarBadge {
  label: string;
  className: string;
  title: string;
}

interface FieldListSidebarProps {
  fields: FieldCoordinates[];
  selectedFieldId: string | null;
  onSelectField: (fieldId: string) => void;
  /** Per-field badge (editor: New/Mod/Saved dirty state; preview: Filled/Empty). */
  badgeById: Record<string, SidebarBadge>;
  displayNameOf: (field: FieldCoordinates, originalIndex: number) => string;
  /** HOW THE LIST IS BROWSED. A view, and nothing more — see utils/fieldSort.ts. */
  prefs: FieldSortPrefs;
  onPrefsChange: (prefs: FieldSortPrefs) => void;
  /** WHAT ORDER THE DOCUMENT GETS. A property of the document, not of the list,
   *  and a different fact from `prefs` however similar the two look here. */
  readingOrderMode: ReadingOrderMode;
  /** Supplying this, together with onReorderFields, makes the mode editable and
   *  turns the list into the reading-order control. Both or neither: a move that
   *  cannot also set the mode to MANUAL would rewrite the array and leave the
   *  document on AUTO, so the author's move would be silently discarded. */
  onReadingOrderModeChange?: (mode: ReadingOrderMode) => void;
  /** localStorage key for the collapsed state — give each instance its own. */
  collapseStorageKey?: string;
  /**
   * Collapse state owned by the PARENT, following the exact precedent the
   * properties inspector set: PDFViewer's left splitter has to disappear when
   * this list retracts, and it cannot react to a state it cannot see. Supply
   * BOTH or NEITHER — the hook here remains the only mechanism and is simply
   * called one level up, one source of truth. LiveFormPreview supplies
   * neither and keeps the self-contained behaviour unchanged.
   */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Fields failing validation — flagged red; they block session save. */
  invalidIds?: Set<string>;
  /** True when rendered inside the editor's out-of-flow left surface.
   *  The parent owns position and height; this component owns its own width,
   *  because only it knows whether it is a 280px panel or a 48px rail. */
  inRail?: boolean;
  /** Replace the whole field array with this new order. Supplying it turns this
   *  list into the reading-order control (D37); omitting it leaves the list
   *  read-only, which is what the preview wants. The array IS the order - there
   *  is no ordinal on a field to update, by design. */
  onReorderFields?: (fields: FieldCoordinates[]) => void;
}

const TYPE_DOT_CLASS: Record<string, string> = {
  email: 'bg-blue-500',
  number: 'bg-green-500',
  date: 'bg-purple-500',
  checkbox: 'bg-orange-500',
};

/** Editor dirty-state badges — exported for PDFViewer to build its badgeById. */
export const DIRTY_BADGE: Record<FieldDirtyStatus, SidebarBadge> = {
  new: {
    label: 'New',
    className: 'bg-blue-100 text-blue-800',
    title: 'Not yet in any saved session — Update Session to persist',
  },
  modified: {
    label: 'Mod',
    className: 'bg-amber-100 text-amber-800',
    title: 'Differs from the last persisted session — Update Session to persist',
  },
  saved: {
    label: 'Saved',
    className: 'bg-green-100 text-green-800',
    title: 'Matches the last persisted session revision',
  },
};

/**
 * Collapsible sidebar listing every drawn field across all pages, replacing
 * the old bottom "Form Fields" grid. Clicking a row selects the field; the
 * parent also navigates the canvas to the field's page.
 *
 * <h2>Two controls, because there are two decisions</h2>
 *
 * SORTING THIS LIST IS BROWSING. It is how an author finds one field among
 * sixty, and it does not touch the document. It used to: the sort key decided
 * readingOrderMode, so sorting by name to FIND a field published alphabetical
 * as the PDF's reading and tab order (WCAG 2.1 SC 2.4.3, Level A). That is
 * why the sort select and the order control below it are separate things with
 * separate labels, and why the panel always says which order the PDF will get.
 *
 * <h2>Taking the reading order over (D37)</h2>
 *
 * Tab order in the generated PDF is structure order, and structure order is
 * the order of this array. AUTO sorts it page-major by geometry, which is right
 * for most forms and SILENTLY WRONG for some: nothing in the geometry says
 * whether a two-column form reads across or down. So the author can take it
 * over - and once they have, the array they leave behind is authoritative and
 * the generator does not re-sort it.
 *
 * They say so in one of two ways, and both are acts of ordering rather than of
 * looking: choose "Manual" in the order control, or move a row. Moving is only
 * offered while the list is actually showing the order the document will get,
 * because a drag in a name-sorted list cannot mean anything about a PDF.
 *
 * Reordering has a KEYBOARD EQUIVALENT and that is not optional: a drag-only
 * control is a WCAG 2.1.1 failure, and shipping one inside an accessibility
 * tool would make our own ATAG Part A position worse, not better (D30).
 * Alt+ArrowUp / Alt+ArrowDown on any row moves that field, the selected row
 * also grows a pair of move buttons, and every move is announced in a polite
 * live region. No tabindex is added to anything - PDFViewer's isTypingTarget
 * guard recognises buttons, and a tabbable div would slip past it and take
 * space-to-pan away from a keyboard user.
 */
const FieldListSidebar: React.FC<FieldListSidebarProps> = ({
  fields,
  selectedFieldId,
  onSelectField,
  badgeById,
  displayNameOf,
  prefs,
  onPrefsChange,
  readingOrderMode,
  onReadingOrderModeChange,
  collapseStorageKey = DEFAULT_COLLAPSED_KEY,
  collapsed: collapsedProp,
  onToggleCollapsed: onToggleCollapsedProp,
  invalidIds,
  inRail = false,
  onReorderFields,
}) => {
  // aria-controls needs a stable id per instance - PDFViewer and LiveFormPreview
  // both render one of these, and two identical ids would point a screen reader at
  // the wrong panel. Colons are legal in an HTML5 id but break CSS selectors, so
  // they come out.
  const instanceId = useId().replace(/:/g, '');
  const panelId = `pfb-field-list-${instanceId}`;
  const sortSelectId = `pfb-field-sort-${instanceId}`;
  // A radio group is grouped by its NAME, so two sidebars sharing one name would
  // let the preview's radios steal the editor's selection.
  const modeGroupName = `pfb-reading-order-mode-${instanceId}`;

  // Moved to a shared hook so the properties inspector collapses the same way,
  // rather than the same way-ish. See hooks/useCollapsiblePanel.ts. The hook
  // runs unconditionally (it is a hook), and the parent's controlled pair wins
  // when supplied — in that case the local state is simply never read.
  const [ownCollapsed, toggleOwnCollapsed] = useCollapsiblePanel(collapseStorageKey);
  const collapsed = collapsedProp ?? ownCollapsed;
  const toggleCollapsed = onToggleCollapsedProp ?? toggleOwnCollapsed;

  // Original array index per field id — creation ordinal for display-name fallback.
  const originalIndexById = useMemo(() => {
    const map: Record<string, number> = {};
    fields.forEach((field, index) => {
      map[field.id] = index;
    });
    return map;
  }, [fields]);

  const sorted = useMemo(() => sortFields(fields, prefs), [fields, prefs]);

  // Announced politely after every move. A drag or an Alt+Arrow that moves a row
  // and says nothing is a change only a sighted user can perceive.
  const [announcement, setAnnouncement] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  // Does the list currently show the sequence the document will get? The panel
  // states the answer, and reordering depends on it.
  const showsGeneratedOrder = listShowsGeneratedOrder(prefs, readingOrderMode);

  // Reordering needs all three: somewhere to send the new array, the ability to
  // set the mode that makes it count, and a list that is showing the real order
  // so a move means what it looks like.
  const canEditOrder = Boolean(onReorderFields) && Boolean(onReadingOrderModeChange);
  const reorderEnabled = canEditOrder && showsGeneratedOrder;

  /** Put the view back on the order the document will get. Offered rather than
   *  done automatically: the author sorted this list for a reason. */
  const showGeneratedOrder = useCallback(() => {
    onPrefsChange(viewShowingGeneratedOrder(readingOrderMode, prefs));
  }, [onPrefsChange, readingOrderMode, prefs]);

  /**
   * The explicit "I am taking this over" / "give it back to geometry" act.
   *
   * Switching TO manual materialises the order the document has RIGHT NOW into
   * the array, so manual starts from the geometric reading order rather than
   * from raw drawing order. Without that, choosing manual would itself be a
   * reordering - the author would ask to own the sequence and be handed a
   * different one, which is D37's original defect wearing a new hat.
   */
  const setMode = useCallback((mode: ReadingOrderMode) => {
    if (!onReadingOrderModeChange || mode === readingOrderMode) return;
    if (mode === 'MANUAL' && onReorderFields) {
      onReorderFields(orderForGeneration(fields, readingOrderMode));
    }
    onReadingOrderModeChange(mode);
    onPrefsChange(viewShowingGeneratedOrder(mode, prefs));
    setAnnouncement(
      mode === 'MANUAL'
        ? 'The PDF reading and tab order is now the order you arrange in this list. It starts from the automatic order.'
        : 'The PDF reading and tab order is now automatic: down each page, then across.'
    );
  }, [onReadingOrderModeChange, readingOrderMode, onReorderFields, fields, onPrefsChange, prefs]);

  /**
   * Move one field to an absolute position in the displayed sequence, and make
   * that sequence the array.
   *
   * This only runs while the list is showing the generated order, so "the
   * displayed sequence" and "the document's sequence" are the same list and
   * what you saw is what you get with your move applied. A move is also an act
   * of ordering, so it takes the order over if the document was still on AUTO -
   * that is D37's affordance and it is kept deliberately.
   */
  const moveTo = useCallback((fieldId: string, targetIndex: number) => {
    if (!onReorderFields || !reorderEnabled) return;
    const from = sorted.findIndex(field => field.id === fieldId);
    if (from < 0) return;
    const to = Math.max(0, Math.min(sorted.length - 1, targetIndex));
    if (to === from) return;

    const next = [...sorted];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorderFields(next);

    const wasAutomatic = readingOrderMode === 'AUTO';
    if (wasAutomatic) {
      onReadingOrderModeChange?.('MANUAL');
      onPrefsChange(viewShowingGeneratedOrder('MANUAL', prefs));
    }

    const name = displayNameOf(moved, originalIndexById[moved.id] ?? 0);
    setAnnouncement(
      `${name} moved to position ${to + 1} of ${next.length}.` +
      (wasAutomatic ? ' The PDF reading and tab order is now manual.' : '')
    );
  }, [onReorderFields, reorderEnabled, sorted, readingOrderMode, onReadingOrderModeChange,
      prefs, onPrefsChange, displayNameOf, originalIndexById]);

  const moveBy = useCallback((fieldId: string, delta: number) => {
    const from = sorted.findIndex(field => field.id === fieldId);
    if (from >= 0) moveTo(fieldId, from + delta);
  }, [sorted, moveTo]);

  // Alt+Arrow, not bare Arrow: bare arrows belong to the list and to the canvas,
  // and Alt is the pairing every desktop reorder control uses.
  const handleRowKeyDown = useCallback((event: React.KeyboardEvent, fieldId: string) => {
    if (!reorderEnabled || !event.altKey) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveBy(fieldId, -1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveBy(fieldId, 1);
    }
  }, [reorderEnabled, moveBy]);

  // Page-major sections when grouping; a single unlabeled section otherwise.
  const sections = useMemo(() => {
    if (!prefs.groupByPage) {
      return [{ page: null as number | null, fields: sorted }];
    }
    const byPage: { page: number | null; fields: FieldCoordinates[] }[] = [];
    sorted.forEach(field => {
      const page = field.pageNumber ?? 1;
      const last = byPage[byPage.length - 1];
      if (last && last.page === page) {
        last.fields.push(field);
      } else {
        byPage.push({ page, fields: [field] });
      }
    });
    return byPage;
  }, [sorted, prefs.groupByPage]);

  if (fields.length === 0) {
    return null;
  }

  if (collapsed) {
    return (
      <button
        onClick={toggleCollapsed}
        title="Show form fields list"
        aria-expanded={false}
        aria-controls={panelId}
        className={`self-start ${inRail ? '' : 'sticky top-4 '}w-12 flex-shrink-0 flex flex-col items-center gap-2 px-2 py-4 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-blue-300 transition-all duration-200`}
      >
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        <span className="text-xs font-bold text-gray-700 [writing-mode:vertical-rl]">
          Fields ({fields.length})
        </span>
      </button>
    );
  }

  return (
    <div
      id={panelId}
      role="region"
      aria-label="Form fields"
      className={`${
        inRail
          // IN THE FLOW, LEFT OF THE PAGE. It overlaid the page until QA on
          // 18 Sep - Scott: "The Form Fields side bar pops out over the PDF,
          // which is not desireable." In the flow, collapsing to the 48px rail
          // genuinely hands its width back to the page, which is what an
          // author expects a collapse control to do and what the overlay could
          // not do.
          //
          // THE WIDTH IS THE PARENT'S NOW, not this component's. PDFViewer
          // puts a keyboard-operable splitter on this edge (W3), and a
          // splitter can only resize a panel that fills the cell it is
          // resizing — a hard-coded w-[280px] here would make the splitter a
          // lie. So in the rail this fills whatever the parent allots
          // (w-full), min-h-0 + flex-1 so the inner list scrolls inside the
          // full-height cell instead of pushing past it. The old 45vh cap is
          // gone WITH the stacked layout it served: below 1024px the list now
          // lives in the tabbed rail beside the page, never above it, so there
          // is no stacked state left to cap.
          ? 'w-full min-h-0 flex-1'
          : 'self-start sticky top-4 w-64 lg:w-72 max-h-[85vh] flex-shrink-0'
      } bg-white border-2 border-gray-200 rounded-xl shadow-sm flex flex-col`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <div className="flex items-center text-sm font-bold text-gray-900">
          <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          Form Fields ({fields.length})
        </div>
        <button
          onClick={toggleCollapsed}
          title="Collapse fields list"
          aria-expanded={true}
          aria-controls={panelId}
          className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* VIEW CONTROLS. Browsing only - these change what this list shows and
          nothing about the document. The label says so, because when it did not
          say so an author sorted by name to find a field and shipped
          alphabetical as the PDF's reading and tab order. */}
      <div className="px-3 py-2 border-b border-gray-200 space-y-2">
        <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-500" htmlFor={sortSelectId}>
          Browse this list by
        </label>
        <select
          id={sortSelectId}
          value={prefs.sortKey}
          onChange={(e) => onPrefsChange({ ...prefs, sortKey: e.target.value as FieldSortKey })}
          title="How this list is sorted. Does not change the PDF."
          className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="reading-order">Reading order (down each page)</option>
          <option value="creation-asc">As arranged (drawing order plus your moves)</option>
          <option value="creation-desc">Newest first</option>
          <option value="name-asc">Name (A → Z)</option>
          <option value="name-desc">Name (Z → A)</option>
        </select>
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={prefs.groupByPage}
            onChange={(e) => onPrefsChange({ ...prefs, groupByPage: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          Group by page
        </label>
        <div className="text-[10px] leading-snug text-gray-400">
          Sorting this list is a way to find a field. It does not change the PDF.
        </div>
      </div>

      {/* THE DOCUMENT'S ORDER. A separate control for a separate decision, and
          it names the mode in force either way - a mode nobody can see is how
          the two came to be one fact in the first place. */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 space-y-1.5">
        {onReadingOrderModeChange ? (
          <fieldset>
            <legend className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
              PDF reading and tab order
            </legend>
            <div className="mt-1 space-y-1">
              <label className="flex items-start gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                <input
                  type="radio"
                  name={modeGroupName}
                  value="AUTO"
                  checked={readingOrderMode === 'AUTO'}
                  onChange={() => setMode('AUTO')}
                  className="mt-0.5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  Automatic
                  <span className="block text-[10px] font-normal text-gray-500">Down each page, then across</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                <input
                  type="radio"
                  name={modeGroupName}
                  value="MANUAL"
                  checked={readingOrderMode === 'MANUAL'}
                  onChange={() => setMode('MANUAL')}
                  className="mt-0.5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  Manual
                  <span className="block text-[10px] font-normal text-gray-500">The order you arrange in this list</span>
                </span>
              </label>
            </div>
          </fieldset>
        ) : (
          // A read-only surface still has to be able to say which order the
          // document gets. Showing nothing here would be a silent mode again.
          <div className="text-[10px] leading-snug text-gray-500">
            <span className="font-bold uppercase tracking-wide">PDF reading and tab order: </span>
            {readingOrderMode === 'MANUAL' ? 'manual, as arranged in the editor' : 'automatic, down each page'}
          </div>
        )}

        {showsGeneratedOrder ? (
          <div className="text-[10px] leading-snug text-gray-400">
            This list is showing that order.
            {reorderEnabled && ' Drag a row, or press Alt+\u2191 / Alt+\u2193 on it, to change it.'}
          </div>
        ) : (
          <div className="text-[10px] leading-snug text-amber-700">
            This list is sorted for browsing, so it is not showing that order.
            {canEditOrder && ' Moving rows is off until it is.'}
            <button
              onClick={showGeneratedOrder}
              className="ml-1 underline font-semibold hover:text-amber-900"
            >
              Show the PDF order
            </button>
          </div>
        )}
      </div>

      {/* Field list */}
      <div className="overflow-y-auto flex-1 py-1">
        {sections.map(section => (
          <div key={section.page ?? 'all'}>
            {section.page !== null && (
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-50 border-y border-gray-100">
                Page {section.page} · {section.fields.length} field{section.fields.length === 1 ? '' : 's'}
              </div>
            )}
            {section.fields.map(field => {
              const badge = badgeById[field.id];
              const isSelected = selectedFieldId === field.id;
              const position = sorted.findIndex(candidate => candidate.id === field.id);
              const name = displayNameOf(field, originalIndexById[field.id] ?? 0);
              return (
                // The row is a DIV wrapping buttons, not a button: a button inside a
                // button is invalid HTML and assistive technology reports it as one
                // control. No tabindex here - the div is not a tab stop and must not
                // become one.
                <div
                  key={field.id}
                  draggable={reorderEnabled}
                  onDragStart={event => {
                    setDraggingId(field.id);
                    event.dataTransfer.effectAllowed = 'move';
                    // Firefox refuses to start a drag without payload.
                    event.dataTransfer.setData('text/plain', field.id);
                  }}
                  onDragOver={event => {
                    if (!reorderEnabled || !draggingId) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    setDropTargetId(field.id);
                  }}
                  onDragLeave={() => setDropTargetId(current => (current === field.id ? null : current))}
                  onDrop={event => {
                    event.preventDefault();
                    if (draggingId && draggingId !== field.id) {
                      moveTo(draggingId, sorted.findIndex(candidate => candidate.id === field.id));
                    }
                    setDraggingId(null);
                    setDropTargetId(null);
                  }}
                  onDragEnd={() => { setDraggingId(null); setDropTargetId(null); }}
                  className={`flex items-center border-l-4 transition-colors duration-150 ${
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'border-transparent hover:bg-blue-50 hover:border-blue-200'
                  } ${draggingId === field.id ? 'opacity-40' : ''} ${
                    dropTargetId === field.id && draggingId !== field.id ? 'ring-2 ring-inset ring-indigo-400' : ''
                  }`}
                >
                  {/* THE GRIP GLYPH — the affordance the owner hunted for. A row
                      that can be dragged looked identical to one that cannot, so
                      the only way to learn a row was movable was to try it. The
                      glyph shows ONLY while reordering is live, because drawing a
                      handle on a row that will not move is a promise the row
                      breaks. aria-hidden, and that is deliberate rather than
                      lazy: Alt+ArrowUp / Alt+ArrowDown is the announced
                      accessible route (stated in the panel's own hint text), and
                      a decorative handle narrated between every row name would
                      only lengthen the list for the people the keyboard route
                      serves. Outside the button so it adds no focus stop. */}
                  {reorderEnabled && (
                    <span
                      aria-hidden="true"
                      data-testid="field-row-grip"
                      className="pl-1.5 text-gray-400 flex-shrink-0 cursor-grab"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <circle cx="7" cy="4" r="1.5" />
                        <circle cx="13" cy="4" r="1.5" />
                        <circle cx="7" cy="10" r="1.5" />
                        <circle cx="13" cy="10" r="1.5" />
                        <circle cx="7" cy="16" r="1.5" />
                        <circle cx="13" cy="16" r="1.5" />
                      </svg>
                    </span>
                  )}
                  <button
                    onClick={() => onSelectField(field.id)}
                    onKeyDown={event => handleRowKeyDown(event, field.id)}
                    // The focus-return target when the properties drawer closes
                    // (W3): PDFViewer's Esc handler needs to put the keyboard
                    // back on "the field being edited", and this row button is
                    // that field's one focusable representative outside the
                    // panel that just closed.
                    data-field-row-id={field.id}
                    title={`${field.type} • Page ${field.pageNumber} • ${Math.round(field.width)}×${Math.round(field.height)}px`}
                    className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2"
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${TYPE_DOT_CLASS[field.type] || 'bg-gray-400'}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-semibold text-gray-900 truncate">
                        {name}
                        {field.required && <span className="ml-1 text-red-500" title="Required">*</span>}
                        {invalidIds?.has(field.id) && (
                          <span
                            className="ml-1.5 inline-block w-2 h-2 rounded-full bg-red-500 align-middle"
                            title="Invalid configuration — fix before saving the session"
                          />
                        )}
                      </span>
                      <span className="block text-[10px] text-gray-500 truncate">
                        {field.type}{!prefs.groupByPage && ` · p.${field.pageNumber}`}
                      </span>
                    </span>
                    {badge && (
                      <span
                        className={`px-1.5 py-0.5 text-[10px] rounded-full font-semibold flex-shrink-0 ${badge.className}`}
                        title={badge.title}
                      >
                        {badge.label}
                      </span>
                    )}
                  </button>

                  {/* Move buttons on the SELECTED row only. Two extra tab stops on every
                      row would treble the length of the list for a keyboard user, and
                      Alt+Arrow already reaches every row; these make the affordance
                      visible and give a pointer user a non-drag route (WCAG 2.5.7). */}
                  {reorderEnabled && isSelected && (
                    <span className="flex flex-col flex-shrink-0 pr-1.5">
                      <button
                        onClick={() => moveBy(field.id, -1)}
                        disabled={position <= 0}
                        aria-label={`Move ${name} earlier in the reading order`}
                        title="Move earlier (Alt+↑)"
                        className="px-1 text-gray-500 hover:text-gray-900 disabled:opacity-25 disabled:hover:text-gray-500"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveBy(field.id, 1)}
                        disabled={position < 0 || position >= sorted.length - 1}
                        aria-label={`Move ${name} later in the reading order`}
                        title="Move later (Alt+↓)"
                        className="px-1 text-gray-500 hover:text-gray-900 disabled:opacity-25 disabled:hover:text-gray-500"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Polite, and OUTSIDE the scrolling list so a move never scrolls it. */}
      <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
    </div>
  );
};

export default FieldListSidebar;
